import { useState, useMemo } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Heart, Hand, Check, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "first_name", label: "FIRST NAME", placeholder: "First Name", half: true, testid: "first-name-input" },
  { name: "last_name", label: "LAST NAME", placeholder: "Last Name", half: true, testid: "last-name-input" },
  { name: "business_name", label: "BUSINESS NAME", placeholder: "Business Name", half: true, testid: "business-name-input" },
  { name: "position_title", label: "POSITION TITLE", placeholder: "Position Title", half: true, testid: "position-title-input" },
  { name: "phone", label: "MOBILE NUMBER", placeholder: "Mobile Number", type: "tel", half: true, testid: "phone-input" },
  { name: "email", label: "EMAIL ADDRESS", placeholder: "Email Address", type: "email", half: true, testid: "email-input" },
];

const OPTIONS = [
  { id: "donate", label: "I want to donate", icon: Heart, testid: "involvement-donate-checkbox" },
  { id: "ambassador", label: "I want to be an ambassador", icon: Users, testid: "involvement-ambassador-checkbox" },
  { id: "volunteer", label: "I want to volunteer", icon: Hand, testid: "involvement-volunteer-checkbox" },
];

const initialForm = { first_name: "", last_name: "", position_title: "", business_name: "", phone: "", email: "" };

const HEART_PATH = "M12 21s-7.5-4.9-9.7-9.2C.7 8.6 2.6 4.9 6.2 4.9c2.2 0 3.9 1.3 4.8 3 .9-1.7 2.6-3 4.8-3 3.6 0 5.5 3.7 3.9 6.9C19.5 16.1 12 21 12 21Z";

const HeartsBurst = () => {
  const hearts = useMemo(
    () =>
      Array.from({ length: 22 }).map((_, i) => ({
        x: (Math.random() - 0.5) * 480,
        y: -(Math.random() * 280 + 60),
        drift: (Math.random() - 0.5) * 60,
        rot: (Math.random() - 0.5) * 300,
        scale: 0.5 + Math.random() * 1.1,
        delay: Math.random() * 0.2,
        duration: 1.4 + Math.random() * 0.8,
        color: Math.random() > 0.35 ? "#E63946" : "#001B3A",
      })),
    []
  );
  return (
    <div data-testid="hearts-confetti" className="pointer-events-none absolute inset-0 overflow-visible" aria-hidden="true">
      {hearts.map((h, i) => (
        <motion.svg
          key={i}
          viewBox="0 0 24 24"
          className="absolute w-5 h-5"
          style={{ left: "50%", top: "35%" }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 0, rotate: 0 }}
          animate={{
            x: [0, h.x * 0.6, h.x + h.drift],
            y: [0, h.y, h.y + 320],
            opacity: [1, 1, 0],
            scale: [0, h.scale, h.scale * 0.8],
            rotate: [0, h.rot / 2, h.rot],
          }}
          transition={{ duration: h.duration, delay: h.delay, ease: "easeOut", times: [0, 0.45, 1] }}
        >
          <path d={HEART_PATH} fill={h.color} />
        </motion.svg>
      ))}
    </div>
  );
};

export const RegisterForm = () => {
  const [form, setForm] = useState(initialForm);
  const [involvements, setInvolvements] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggle = (id) =>
    setInvolvements((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill in your name, mobile number and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/registrations`, { ...form, involvements });
      setSubmitted(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail)
        ? "Please check your details — the email address doesn't look valid."
        : typeof detail === "string"
          ? detail
          : "Couldn't reach the server just then — please check your internet and try again.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="registration-section" className="relative">
      <AnimatePresence mode="wait">
        {submitted ? (
          <motion.div
            key="success"
            data-testid="registration-success-message"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden border-2 border-navy bg-white p-10 md:p-14"
          >
            <HeartsBurst />
            <HeartHandshake className="w-12 h-12 text-brandred mb-6" strokeWidth={1.5} />
            <h3 className="font-display text-3xl md:text-4xl text-navy uppercase mb-4">
              You're in, {form.first_name}.
            </h3>
            <p className="text-steel text-base md:text-lg max-w-md">
              Thank you for registering your interest. We'll be in touch soon about how your
              settlement can become someone's tomorrow.
            </p>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            data-testid="registration-form"
            noValidate
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            exit={{ opacity: 0, y: -20 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map((f, i) => (
                <motion.div
                  key={f.name}
                  className={f.half ? "" : "sm:col-span-2"}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}
                >
                  <label htmlFor={f.testid} className="block text-xs font-semibold tracking-[0.18em] text-steel mb-1">
                    {f.label}
                  </label>
                  <input
                    id={f.testid}
                    data-testid={f.testid}
                    type={f.type || "text"}
                    placeholder={f.placeholder}
                    value={form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full h-10 bg-white border border-navy rounded-none px-4 text-sm text-navy placeholder:text-navy/35 outline-none transition-[border-width,box-shadow] duration-200 focus:border-2 focus:shadow-[4px_4px_0_0_#E63946]"
                  />
                </motion.div>
              ))}
            </div>

            <div className="mt-4">
              <p data-testid="involvement-heading" className="font-display text-lg md:text-xl text-navy uppercase tracking-wide border-b-2 border-navy pb-2 mb-1">
                How would you like to get involved?
              </p>
              <div className="flex flex-col">
                {OPTIONS.map((opt) => {
                  const checked = involvements.includes(opt.id);
                  const Icon = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      data-testid={opt.testid}
                      onClick={() => toggle(opt.id)}
                      className="group flex items-center gap-3 py-1 border-b border-navy/15 text-left transition-colors duration-200 hover:bg-white/60"
                    >
                      <span
                        className={`w-6 h-6 shrink-0 border-2 border-navy flex items-center justify-center transition-colors duration-200 ${
                          checked ? "bg-brandred border-brandred" : "bg-white group-hover:border-brandred"
                        }`}
                      >
                        {checked && <Check className="w-4 h-4 text-cream" strokeWidth={3} />}
                      </span>
                      <Icon className={`w-5 h-5 shrink-0 transition-colors duration-200 ${checked ? "text-brandred" : "text-navy/50"}`} strokeWidth={1.75} />
                      <span className={`text-sm md:text-base transition-colors duration-200 ${checked ? "text-navy font-medium" : "text-navy/80"}`}>
                        {opt.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <motion.button
              type="submit"
              data-testid="register-interest-submit-button"
              disabled={submitting}
              whileTap={{ scale: 0.985 }}
              className="group relative mt-4 w-full h-12 overflow-hidden bg-navy text-cream font-display text-lg md:text-xl tracking-wider uppercase disabled:opacity-70"
            >
              <span className="absolute inset-0 bg-brandred translate-y-full transition-transform duration-300 ease-out group-hover:translate-y-0" />
              <span className="relative flex items-center justify-center gap-3">
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {submitting ? "Registering..." : "Register Your Interest"}
              </span>
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RegisterForm;
